export { useExpenseAccounts } from './useExpenseAccounts';
export { useProcurementSearch } from './useProcurementSearch';
export { useCustomerJobSearch } from './useCustomerJobSearch';
export { useVehicleSearch } from './useVehicleSearch';
export { SupplierPOSelector } from './SupplierPOSelector';
export { CustomerJobSelector, JobSelector } from './CustomerJobSelector';
export { VehicleSelector } from './VehicleSelector';
export { ReceiptCaptureSection } from './ReceiptCaptureSection';
export { ExpenseLineItemsEditor } from './ExpenseLineItemsEditor';
export {
  EXPENSE_ENTRY_TYPES,
  ENTRY_CONFIG,
  getReceiptCategoryMismatchInfo,
  getInitialFormData,
  PURCHASE_CATEGORY_OPTIONS,
  CAPTURE_PROFILE_OPTIONS,
  CHILD_ENTRY_FORM_Z_INDEX,
  // Registry-backed accessors (YAML → hardcoded fallback)
  getEntryTypeConfig,
  getExpenseEntryTypes,
  getDefaultPaymentMethod,
  getPurchaseCategoryOptions,
  getCaptureProfileOptions,
  getChildEntryFormZIndex,
  getReceiptCategoryExpected,
} from './constants';
export type {
  EntryFieldConfig,
  EntryTypeConfig,
} from './constants';
export type {
  Supplier,
  PurchaseOrder,
  PoLineItem,
  PoFulfillment,
} from './useProcurementSearch';
export type {
  Customer,
  Job,
} from './useCustomerJobSearch';
export type {
  Vehicle,
} from './useVehicleSearch';

// Domain logic (extracted from ChildEntryForm)
export { resolveDefaultTaxRate, snapToStandardVatRate, detectVatTreatment, detectMixedVatSplit, hasMixedVatTreatment } from './taxResolution';
export type { TaxResolutionInput, TaxResolutionResult, VatTreatmentResult, MixedVatSplit } from './taxResolution';
export { buildBomLineItems, toIntakeLineItems } from './lineItemBuilder';
export type { BomLineItem, LineItemBuilderInput } from './lineItemBuilder';

// Intra-receipt duplicate line item detection (overlapping multi-photo capture)
export {
  detectDuplicateLineItems,
  deduplicateLineItems,
  normalizeDescription,
} from './lineItemDedup';
export type {
  DuplicateLineItemGroup,
  DetectDuplicateLineItemsResult,
} from './lineItemDedup';

// Capture policy evaluation (pure function, testable without React)
export { evaluateCapturePolicy } from './capturePolicy';
export type { CapturePolicyInput, CapturePolicyResult } from './capturePolicy';

// Duplicate evidence detection (pure function, testable without React)
export { checkDuplicateEvidence } from './duplicateEvidence';
export type { DuplicateMatch, DuplicateEvidenceResult } from './duplicateEvidence';

// Capture session hook (headless state machine for form submission)
export { useCaptureSession } from './useCaptureSession';
export type { UseCaptureSessionReturn, CaptureSessionSubmitInput, ChildEntryFormData } from './useCaptureSession';

// Draft persistence with crash recovery and resume-after-auth
export { useCapturePersistence } from './useCapturePersistence';
export type { UseCapturePersistenceParams, UseCapturePersistenceReturn, CaptureDraft, RestoredDraft } from './useCapturePersistence';

// Receipt analysis hook (AI extraction, form auto-population, duplicate detection)
export { useReceiptAnalysis } from './useReceiptAnalysis';
export type { UseReceiptAnalysisReturn, UseReceiptAnalysisParams, AutoSwitchBanner } from './useReceiptAnalysis';

// PDF import hook (client-side pdf.js → base64 images for receipt photos)
export { usePdfImport } from './usePdfImport';
export type { UsePdfImportReturn } from './usePdfImport';

// Image import hook (file picker → downscaled base64 JPEGs for receipt photos)
export { useImageImport } from './useImageImport';
export type { UseImageImportReturn } from './useImageImport';

// Vehicle info hook (fleet API fetch + realtime refresh via WebSocket)
export { useVehicleInfo } from './useVehicleInfo';
export type { UseVehicleInfoReturn, UseVehicleInfoParams, VehicleInfo } from './useVehicleInfo';

// Last fuel entry hook (fetches previous fuel entry for insights: distance, efficiency, price/litre)
export { useLastFuelEntry } from './useLastFuelEntry';
export type { LastFuelEntry, UseLastFuelEntryParams, UseLastFuelEntryReturn } from './useLastFuelEntry';

// Capture mode buttons hook (2×2 camera grid: configurable expense slots vs camera controls)
export { useCaptureModeButtons } from './useCaptureModeButtons';
export type { UseCaptureModeButtonsReturn, ConfigurableEntryType } from './useCaptureModeButtons';

// Linkable work item docs hook (fetches linked financial documents for the active appointment)
export { useLinkableWorkItemDocs } from './useLinkableWorkItemDocs';
export type { UseLinkableWorkItemDocsReturn } from './useLinkableWorkItemDocs';

// Discriminated form schemas by entry type (replaces Record<string, string>)
export {
  createInitialFormData,
  getField,
  setField,
  isValidFieldForType,
  getEntryType,
  toRecordString,
  fromRecordString,
  FULL_TRUST_CAPABILITIES,
  ZERO_TRUST_CAPABILITIES,
  resolveCapabilities,
} from './captureSchema';
export type {
  CaptureFormData,
  CaptureFieldKey,
  SchemaForType,
  FieldKeysForType,
  TollFormData,
  FuelFormData,
  MaintenanceFormData,
  SiteVisitFormData,
  AssessmentFormData,
  SupervisionFormData,
  TrainingFormData,
  RouteStopFormData,
  PurchaseFormData,
  DeliveryNoteFormData,
  SupplierInvoiceFormData,
  GrnFormData,
  PurchaseReturnFormData,
  CasualWageFormData,
  CaptureCapabilities,
} from './captureSchema';
